import { useState, useEffect } from "react";
import { getTestimonials, submitTestimonial, type TestimonialItem } from "../utils/adminStore";

export function Testimonials() {
  const [testimonials, setTestimonials] = useState<TestimonialItem[]>([]);
  const [activeIndex, setActiveIndex] = useState(0);
  const [modalOpen, setModalOpen] = useState(false);

  // Form State
  const [name, setName] = useState("");
  const [role, setRole] = useState("");
  const [organization, setOrganization] = useState("");
  const [sector, setSector] = useState("IT & Enterprise SaaS");
  const [rating, setRating] = useState(5);
  const [content, setContent] = useState("");
  const [submitted, setSubmitted] = useState(false);

  useEffect(() => {
    const sync = () => {
      const all = getTestimonials();
      const approved = all.filter((t) => t.status === "approved");
      setTestimonials(approved);
    };
    sync();
    window.addEventListener("radiece_admin_update", sync);
    return () => window.removeEventListener("radiece_admin_update", sync);
  }, []);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!name || !organization || !content) return;

    submitTestimonial({
      name,
      role: role || "Client / Trainee",
      organization,
      sector,
      rating,
      content,
    });

    setSubmitted(true);
    setName("");
    setRole("");
    setOrganization("");
    setContent("");
  };

  const current = testimonials[activeIndex];

  const nextTestimonial = () => {
    if (testimonials.length === 0) return;
    setActiveIndex((prev) => (prev + 1) % testimonials.length);
  };

  const prevTestimonial = () => {
    if (testimonials.length === 0) return;
    setActiveIndex((prev) => (prev - 1 + testimonials.length) % testimonials.length);
  };

  return (
    <section className="no-print bg-slate-900 py-16 sm:py-24 text-white relative overflow-hidden">
      {/* Background Mesh */}
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top_right,_var(--tw-gradient-stops))] from-blue-900/30 via-slate-900 to-slate-950 pointer-events-none" />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        <div className="text-center mb-12 sm:mb-16">
          <span className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-primary/10 border border-primary/20 text-primary text-xs font-semibold uppercase tracking-wider mb-3">
            Client & Partner Feedback
          </span>
          <h2 className="text-3xl sm:text-4xl font-extrabold tracking-tight text-white">
            Verified Partner Reviews & Testimonials
          </h2>
          <p className="mt-4 text-base sm:text-lg text-slate-400 max-w-2xl mx-auto leading-relaxed font-light">
            Real feedback from enterprise clients, corporate partners, and engineering leadership.
          </p>

          <div className="mt-6">
            <button
              onClick={() => {
                setModalOpen(true);
                setSubmitted(false);
              }}
              className="inline-flex items-center gap-2 bg-white/10 hover:bg-white/20 text-white font-bold px-5 py-2.5 rounded-full text-xs transition-all border border-white/20 cursor-pointer shadow-md"
            >
              <span>✍️</span> Submit Your Review / Feedback
            </button>
          </div>
        </div>

        {/* Testimonials Display */}
        {testimonials.length > 0 && current ? (
          <div className="max-w-4xl mx-auto">
            <div className="bg-[#101c2a] rounded-2xl border border-white/15 p-6 sm:p-10 shadow-2xl relative transition-all duration-300">
              <div className="flex flex-wrap items-center justify-between gap-4 mb-6">
                <span className="px-3 py-1 rounded-md bg-white/10 border border-white/15 text-xs font-bold text-primary">
                  {current.sector}
                </span>
                <div className="flex items-center gap-1">
                  {[...Array(current.rating)].map((_, i) => (
                    <svg key={i} className="w-5 h-5 text-amber-400 fill-amber-400" viewBox="0 0 20 20">
                      <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                    </svg>
                  ))}
                </div>
              </div>

              <blockquote className="text-lg sm:text-xl text-slate-200 font-medium leading-relaxed mb-8 italic">
                "{current.content}"
              </blockquote>

              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-6 pt-6 border-t border-white/10">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 rounded-full bg-primary/20 border border-primary/40 text-primary flex items-center justify-center font-bold text-lg shadow-md">
                    {current.name.charAt(0)}
                  </div>
                  <div>
                    <h4 className="text-base font-bold text-white">{current.name}</h4>
                    <p className="text-xs sm:text-sm text-slate-400">
                      {current.role} • <span className="text-slate-300 font-medium">{current.organization}</span>
                    </p>
                  </div>
                </div>

                <div className="flex items-center gap-2 self-end sm:self-center">
                  <button
                    onClick={prevTestimonial}
                    aria-label="Previous Testimonial"
                    className="w-10 h-10 rounded-lg bg-white/10 hover:bg-white/20 text-white flex items-center justify-center border border-white/20 transition-colors cursor-pointer"
                  >
                    ←
                  </button>
                  <span className="text-xs font-mono font-semibold text-slate-400 px-2">
                    {activeIndex + 1} / {testimonials.length}
                  </span>
                  <button
                    onClick={nextTestimonial}
                    aria-label="Next Testimonial"
                    className="w-10 h-10 rounded-lg bg-white/10 hover:bg-white/20 text-white flex items-center justify-center border border-white/20 transition-colors cursor-pointer"
                  >
                    →
                  </button>
                </div>
              </div>
            </div>

            <div className="flex justify-center items-center gap-2 mt-6">
              {testimonials.map((_, idx) => (
                <button
                  key={idx}
                  onClick={() => setActiveIndex(idx)}
                  className={`h-2.5 rounded-full transition-all duration-300 ${
                    activeIndex === idx ? "w-8 bg-primary" : "w-2.5 bg-white/20 hover:bg-white/40"
                  }`}
                />
              ))}
            </div>
          </div>
        ) : (
          <div className="text-center py-12 bg-[#101c2a] rounded-2xl border border-white/10 max-w-xl mx-auto p-6">
            <p className="text-slate-300 text-sm mb-4">No public testimonials currently published.</p>
            <button
              onClick={() => setModalOpen(true)}
              className="bg-primary text-slate-950 font-bold px-6 py-2.5 rounded-xl text-xs uppercase"
            >
              Submit First Review →
            </button>
          </div>
        )}
      </div>

      {/* Public Testimonial Submission Modal */}
      {modalOpen && (
        <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur-xs flex items-center justify-center p-4">
          <div className="bg-[#101c2a] border border-white/20 rounded-2xl max-w-lg w-full p-6 sm:p-8 shadow-2xl text-left relative animate-in">
            <button
              onClick={() => setModalOpen(false)}
              className="absolute top-4 right-4 text-slate-400 hover:text-white text-lg font-bold p-1 cursor-pointer"
            >
              ✕
            </button>

            {submitted ? (
              <div className="text-center py-8 space-y-4">
                <div className="w-14 h-14 bg-emerald-500/20 text-emerald-400 rounded-full flex items-center justify-center mx-auto text-2xl font-bold border border-emerald-500/30">
                  ✓
                </div>
                <h3 className="text-xl font-bold text-white">Review Submitted for Review!</h3>
                <p className="text-xs text-slate-300 leading-relaxed">
                  Thank you! Your testimonial has been submitted to RADIECE administration. It will appear publicly on the homepage once reviewed and approved.
                </p>
                <button
                  onClick={() => setModalOpen(false)}
                  className="bg-primary hover:bg-sky-400 text-slate-950 font-bold px-6 py-2.5 rounded-xl text-xs uppercase tracking-wider mt-2 cursor-pointer"
                >
                  Close Window
                </button>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <h3 className="text-lg font-bold text-white">Submit Partner / Trainee Review</h3>
                  <p className="text-xs text-slate-300 mt-1">
                    Your feedback will be reviewed by our administration before public approval.
                  </p>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div>
                    <label className="block text-xs font-bold text-slate-300 uppercase tracking-wider mb-1">
                      Your Name *
                    </label>
                    <input
                      type="text"
                      required
                      value={name}
                      onChange={(e) => setName(e.target.value)}
                      placeholder="e.g. John Doe"
                      className="w-full px-3 py-2 bg-white/10 border border-white/20 rounded-lg text-xs text-white placeholder-slate-400 focus:outline-none focus:border-primary"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-bold text-slate-300 uppercase tracking-wider mb-1">
                      Title / Role
                    </label>
                    <input
                      type="text"
                      value={role}
                      onChange={(e) => setRole(e.target.value)}
                      placeholder="e.g. CTO / Engineering Student"
                      className="w-full px-3 py-2 bg-white/10 border border-white/20 rounded-lg text-xs text-white placeholder-slate-400 focus:outline-none focus:border-primary"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div>
                    <label className="block text-xs font-bold text-slate-300 uppercase tracking-wider mb-1">
                      Company / Organization *
                    </label>
                    <input
                      type="text"
                      required
                      value={organization}
                      onChange={(e) => setOrganization(e.target.value)}
                      placeholder="e.g. TechCorp / University"
                      className="w-full px-3 py-2 bg-white/10 border border-white/20 rounded-lg text-xs text-white placeholder-slate-400 focus:outline-none focus:border-primary"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-bold text-slate-300 uppercase tracking-wider mb-1">
                      Sector / Vertical
                    </label>
                    <select
                      value={sector}
                      onChange={(e) => setSector(e.target.value)}
                      className="w-full px-3 py-2 bg-[#101c2a] border border-white/20 rounded-lg text-xs text-white focus:outline-none focus:border-primary"
                    >
                      <option value="IT & Enterprise SaaS">IT & Enterprise SaaS</option>
                      <option value="Education & Upskilling">Education & Upskilling</option>
                      <option value="Security & Surveillance">Security & Surveillance</option>
                      <option value="Hardware & R&D">Hardware & R&D</option>
                      <option value="Robotics & Automation">Robotics & Automation</option>
                    </select>
                  </div>
                </div>

                <div>
                  <label className="block text-xs font-bold text-slate-300 uppercase tracking-wider mb-1">
                    Rating (1 to 5 Stars)
                  </label>
                  <select
                    value={rating}
                    onChange={(e) => setRating(Number(e.target.value))}
                    className="w-full px-3 py-2 bg-[#101c2a] border border-white/20 rounded-lg text-xs text-white focus:outline-none focus:border-primary"
                  >
                    <option value={5}>⭐⭐⭐⭐⭐ 5 Stars (Excellent)</option>
                    <option value={4}>⭐⭐⭐⭐ 4 Stars (Very Good)</option>
                    <option value={3}>⭐⭐⭐ 3 Stars (Good)</option>
                  </select>
                </div>

                <div>
                  <label className="block text-xs font-bold text-slate-300 uppercase tracking-wider mb-1">
                    Your Review / Feedback *
                  </label>
                  <textarea
                    rows={4}
                    required
                    value={content}
                    onChange={(e) => setContent(e.target.value)}
                    placeholder="Describe your experience with RADIECE engineering, courses, or technical support..."
                    className="w-full px-3 py-2 bg-white/10 border border-white/20 rounded-lg text-xs text-white placeholder-slate-400 focus:outline-none focus:border-primary resize-none"
                  />
                </div>

                <button
                  type="submit"
                  className="w-full bg-primary hover:bg-sky-400 text-slate-950 font-bold py-3 rounded-xl transition-all text-xs uppercase tracking-wider shadow-lg cursor-pointer"
                >
                  Submit Review for Approval →
                </button>
              </form>
            )}
          </div>
        </div>
      )}
    </section>
  );
}
